/* Reports — the nightly post-mortem viewer (read-only).

   Left: the index from the bucket (date, title, cost, daily/weekly) with a
   heartbeat badge. Right: the selected report rendered from markdown with
   GFM tables, a "Copy handoff" button for the fenced HANDOFF block, and
   the attachments (code/data) as presigned, short-lived links. */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, List, ListItemButton, ListItemText, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, DownloadOutlined, RefreshOutlined } from '@mui/icons-material';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { C, MONO } from '../../theme';
import {
  extractHandoff, heartbeatAgeHours, reportsApi,
  type Heartbeat, type ReportDoc, type ReportFile, type ReportIndexEntry,
} from '../../services/reports';

marked.setOptions({ gfm: true, breaks: false });

const fmtUsd = (v: number | null | undefined) => (v == null ? '—' : `$${v.toFixed(2)}`);
const fmtBytes = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

/* Markdown → sanitized HTML. The content is our own agent's output behind
   login, but it is still rendered as HTML, so it goes through DOMPurify. */
function renderMarkdown(md: string): string {
  const html = marked.parse(md, { async: false }) as string;
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

const HeartbeatBadge: React.FC<{ hb: Heartbeat | null }> = ({ hb }) => {
  const age = heartbeatAgeHours(hb);
  if (age == null) return <Chip size="small" label="no heartbeat" sx={{ bgcolor: C.neutral, color: C.ink2 }} />;
  const day = new Date().getDay();
  const stale = age > 30 && day >= 1 && day <= 5; // a weekday with no run since yesterday evening
  const label = age < 1 ? `ran ${Math.round(age * 60)} min ago` : `ran ${age.toFixed(0)} h ago`;
  return (
    <Tooltip title={hb?.at ? `last run ${new Date(hb.at).toLocaleString()}${hb.host ? ` on ${hb.host}` : ''}` : ''}>
      <Chip size="small" label={label}
        sx={{ bgcolor: stale ? 'rgba(230,103,103,.15)' : 'rgba(53,196,141,.15)', color: stale ? C.neg : C.pos, fontFamily: MONO }} />
    </Tooltip>
  );
};

const Reports: React.FC = () => {
  const [entries, setEntries] = useState<ReportIndexEntry[]>([]);
  const [heartbeat, setHeartbeat] = useState<Heartbeat | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [doc, setDoc] = useState<ReportDoc | null>(null);
  const [files, setFiles] = useState<ReportFile[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true); setError(null);
    try {
      const res = await reportsApi.list();
      setEntries(res.reports || []);
      setHeartbeat(res.heartbeat || null);
      setSelected((cur) => cur ?? res.reports?.[0]?.date ?? null);
    } catch (e: any) {
      setError(e?.status === 503 ? 'Reports bucket is not configured on the server.' : (e?.message || 'Failed to load reports'));
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoadingDoc(true); setDoc(null); setFiles([]); setCopied(false);
    (async () => {
      try {
        const [d, f] = await Promise.all([reportsApi.get(selected), reportsApi.files(selected)]);
        if (!cancelled) { setDoc(d); setFiles(f.files || []); }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load the report');
      } finally {
        if (!cancelled) setLoadingDoc(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const html = useMemo(() => (doc ? renderMarkdown(doc.markdown) : ''), [doc]);
  const handoff = useMemo(() => (doc ? extractHandoff(doc.markdown) : null), [doc]);

  /* Clipboard access is denied in some contexts (no user-gesture focus,
     embedded panes, older browsers). Try the async API, then the legacy
     execCommand path, and if both fail show the block as selectable text
     so the operator can still take it by hand. */
  const copyHandoff = async () => {
    if (!handoff) return;
    try {
      await navigator.clipboard.writeText(handoff);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    } catch { /* fall through */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = handoff;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch { /* fall through */ }
    setShowHandoff(true);
  };

  const openFile = async (path: string) => {
    if (!selected) return;
    const { url } = await reportsApi.link(selected, path);
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: C.ink, fontWeight: 600 }}>Reports</Typography>
          <Typography variant="body2" sx={{ color: C.ink3 }}>Nightly post-mortems from the research agent. Read-only.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <HeartbeatBadge hb={heartbeat} />
          <Button size="small" startIcon={<RefreshOutlined />} onClick={loadList} sx={{ color: C.ink2 }}>Refresh</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {/* minmax(0, 1fr): a grid column's default minimum is the content's
          intrinsic width, so a wide table would widen the page instead of
          scrolling inside its own box. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '280px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        <Paper sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, maxHeight: { md: 'calc(100vh - 160px)' }, overflow: 'auto' }}>
          {loadingList ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          ) : entries.length === 0 ? (
            <Typography sx={{ p: 2, color: C.ink3 }}>No reports yet.</Typography>
          ) : (
            <List dense disablePadding>
              {entries.map((e) => (
                <ListItemButton key={e.date} selected={e.date === selected} onClick={() => setSelected(e.date)}
                  sx={{ borderBottom: `1px solid ${C.line}`, '&.Mui-selected': { bgcolor: 'rgba(57,135,229,.12)' } }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontFamily: MONO, color: C.ink, fontSize: 13 }}>{e.date}</Typography>
                        {e.weekly && <Chip size="small" label="weekly" sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(139,92,246,.18)', color: '#B9A3F5' }} />}
                        {e.subtype && e.subtype !== 'success' && <Chip size="small" label={e.subtype} sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(230,103,103,.15)', color: C.neg }} />}
                      </Stack>
                    }
                    secondary={<Typography sx={{ color: C.ink3, fontSize: 12 }} noWrap>{e.title} · {fmtUsd(e.costUsd)}</Typography>}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        <Paper sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, p: { xs: 2, md: 3 }, minHeight: 300, minWidth: 0, overflow: 'hidden' }}>
          {loadingDoc ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          ) : !doc ? (
            <Typography sx={{ color: C.ink3 }}>Select a report.</Typography>
          ) : (
            <>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip size="small" label={doc.date} sx={{ fontFamily: MONO, bgcolor: C.neutral, color: C.ink }} />
                {doc.runResult && (
                  <Typography sx={{ color: C.ink3, fontSize: 12, fontFamily: MONO }}>
                    {String((doc.runResult as any).model ?? '')} · {String((doc.runResult as any).numTurns ?? '?')} turns · {fmtUsd((doc.runResult as any).costUsd ?? null)}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title={handoff ? 'Copy the HANDOFF block to paste into Claude Code' : 'This report has no HANDOFF block'}>
                  <span>
                    <Button size="small" variant="outlined" startIcon={<ContentCopy />} disabled={!handoff} onClick={copyHandoff}>
                      {copied ? 'Copied' : 'Copy handoff'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              <Dialog open={showHandoff} onClose={() => setShowHandoff(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>Handoff block (clipboard unavailable here; select and copy)</DialogTitle>
                <DialogContent>
                  <Box component="pre" sx={{ fontFamily: MONO, fontSize: 12, whiteSpace: 'pre-wrap', userSelect: 'text', m: 0 }}>
                    {handoff}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowHandoff(false)}>Close</Button>
                </DialogActions>
              </Dialog>

              <Box
                className="report-md"
                dangerouslySetInnerHTML={{ __html: html }}
                sx={{
                  color: C.ink, fontSize: 14, lineHeight: 1.55, minWidth: 0, overflowWrap: 'anywhere',
                  '& h1': { fontSize: 22, mt: 1, mb: 1.5 }, '& h2': { fontSize: 18, mt: 3, mb: 1, color: C.ink },
                  '& h3': { fontSize: 15, mt: 2, mb: 1, color: C.ink2 },
                  '& p, & li': { color: C.ink2 }, '& strong': { color: C.ink },
                  '& code': { fontFamily: MONO, fontSize: 12.5, bgcolor: C.panel2, px: 0.5, borderRadius: 0.5 },
                  '& pre': { fontFamily: MONO, fontSize: 12, bgcolor: C.bg, border: `1px solid ${C.line}`, p: 1.5, borderRadius: 1, overflowX: 'auto' },
                  '& pre code': { bgcolor: 'transparent', p: 0 },
                  '& table': { borderCollapse: 'collapse', my: 1.5, display: 'block', overflowX: 'auto', maxWidth: '100%', fontSize: 13 },
                  '& th, & td': { border: `1px solid ${C.line2}`, px: 1, py: 0.5, textAlign: 'left', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
                  '& th': { bgcolor: C.panel2, color: C.ink, fontWeight: 600 },
                  '& hr': { border: 0, borderTop: `1px solid ${C.line}`, my: 2 },
                  '& a': { color: C.accent },
                }}
              />

              {files.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: C.line }} />
                  <Typography variant="subtitle2" sx={{ color: C.ink2, mb: 1 }}>Attachments (links expire in 10 minutes)</Typography>
                  <Stack spacing={0.5}>
                    {files.filter((f) => f.path !== 'report.md').map((f) => (
                      <Stack key={f.path} direction="row" spacing={1} alignItems="center">
                        <Button size="small" startIcon={<DownloadOutlined />} onClick={() => openFile(f.path)}
                          sx={{ fontFamily: MONO, fontSize: 12, color: C.accent, textTransform: 'none', justifyContent: 'flex-start' }}>
                          {f.path}
                        </Button>
                        <Typography sx={{ color: C.ink3, fontSize: 11, fontFamily: MONO }}>{fmtBytes(f.size)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Reports;
